import type { TradeIdea } from '@/features/home/lib/home-ideas'
import { formatPrice } from '@/features/home/lib/home-ideas'
import type { Exchange } from '@/types/generated/common/types'

/**
 * Opportunistic trade ideas (Alex and John's second bucket): new positions, picked from a
 * small playbook of setups in five families — momentum, mean reversion, arbitrage,
 * fundamental and news — and matched to what the user cares about (coins they trade, watch,
 * hold or are looking at). Each comes with the trade written out (entry, stop and target, sized
 * so the stop risks about half a percent of the account) for the Order Form to review, or the
 * two legs for the Arb builder. Pure (tested in __tests__/home-opportunities.test.ts).
 */

export type OpportunityCategory =
  | 'momentum'
  | 'meanReversion'
  | 'arbitrage'
  | 'fundamental'
  | 'news'

export type SetupId =
  | 'breakout'
  | 'breakdown'
  | 'overextended'
  | 'crowdedFunding'
  | 'fundingArb'
  | 'etfFlows'
  | 'unlockPressure'
  | 'newsMove'

export interface PlaybookSetup {
  id: SetupId
  category: OpportunityCategory
}

/** The directory of setups Home looks for, in display order (texts live in the messages). */
export const PLAYBOOK: readonly PlaybookSetup[] = [
  { id: 'breakout', category: 'momentum' },
  { id: 'breakdown', category: 'momentum' },
  { id: 'overextended', category: 'meanReversion' },
  { id: 'crowdedFunding', category: 'meanReversion' },
  { id: 'fundingArb', category: 'arbitrage' },
  { id: 'etfFlows', category: 'fundamental' },
  { id: 'unlockPressure', category: 'fundamental' },
  { id: 'newsMove', category: 'news' },
]

export const CATEGORIES: readonly OpportunityCategory[] = [
  'momentum',
  'meanReversion',
  'arbitrage',
  'fundamental',
  'news',
]

export type ScreenerFlagName =
  | 'pumping'
  | 'dumping'
  | 'highVol'
  | 'squeeze'
  | 'oiSurge'
  | 'thinBook'
  | 'fundingExtreme'

/** One coin's live market read, from the screener stream. */
export interface MarketSignal {
  asset: string
  price: number
  change24h: number | null
  change15m: number | null
  /** 15-minute standard deviation of returns, percent. */
  stdev15m: number | null
  /** Share of 15-minute volume that was taker buys, percent. */
  takerBuyPct15m: number | null
  liqLongUsd15m: number | null
  liqShortUsd15m: number | null
  /** OI-weighted funding, annualised percent. */
  fundingApr: number | null
  oiChange1h: number | null
  oiChange24h: number | null
  /** Open interest across venues, USD. */
  oiUsd: number | null
  flags: ReadonlySet<ScreenerFlagName>
  venues: readonly { exchange: Exchange; symbol: string; fundingApr: number | null }[]
}

export type RelevanceReason = 'held' | 'traded' | 'watched' | 'viewed'

export interface Relevance {
  reason: RelevanceReason
  /** 0–1: how much this coin matters to the user. */
  weight: number
  /** The venue they trade it on, when known. */
  exchange?: Exchange
}

export interface OpportunityInput {
  signals: readonly MarketSignal[]
  relevance: ReadonlyMap<string, Relevance>
  /** An order-eligible account per venue the user can trade on. */
  accountsByExchange: ReadonlyMap<Exchange, string>
  /** Coins the user currently has a position in (and its side). */
  heldSides: ReadonlyMap<string, 'long' | 'short'>
  /** Daily net ETF flows (USD), oldest first, per coin. */
  etfFlows?: ReadonlyMap<string, readonly number[]>
  unlocks?: readonly { asset: string; at: number; valueUsd: number; pctOfMcap: number | null }[]
  /** Recent headlines naming a coin. */
  news?: readonly { asset: string; title: string; at: number }[]
  equityUsd: number | null
  now: number
}

export const MAX_OPPORTUNITIES = 4
const MAX_PER_CATEGORY = 2
const RISK_SHARE = 0.005
const MAX_NOTIONAL_SHARE = 0.25
const MIN_NOTIONAL = 25
const DEFAULT_NOTIONAL = 100
/** A coin that matters to nobody needs a much stronger signal to be shown… */
const MARKET_WEIGHT = 0.25
const MARKET_MIN_PRIORITY = 55
/** …and a deep market: small caps with extreme readings are noise for most users. */
const MARKET_MIN_OI_USD = 500e6
const MIN_ARB_SPREAD_APR = 15
/** An arb on a coin the user doesn't follow needs a deep market behind it. */
const MIN_ARB_OI_USD = 50e6
const NEWS_WINDOW_MS = 6 * 3600_000
const UNLOCK_WINDOW_MS = 7 * 86_400_000

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

/** Notional for a stop `stopDistance` (fraction) away, risking ~0.5% of the account. */
export function sizeForRisk(equityUsd: number | null, stopDistance: number): number {
  if (!equityUsd || equityUsd <= 0 || stopDistance <= 0) return DEFAULT_NOTIONAL
  const notional = (equityUsd * RISK_SHARE) / stopDistance
  return Math.round(Math.max(MIN_NOTIONAL, Math.min(notional, equityUsd * MAX_NOTIONAL_SHARE)))
}

function venueFor(
  signal: MarketSignal,
  relevance: Relevance | undefined,
  accounts: ReadonlyMap<Exchange, string>
): { exchange: Exchange; symbol: string; accountId: string } | null {
  const tradable = signal.venues.filter((venue) => accounts.has(venue.exchange))
  const preferred =
    tradable.find((venue) => venue.exchange === relevance?.exchange) ?? tradable[0] ?? null
  return preferred ? { ...preferred, accountId: accounts.get(preferred.exchange)! } : null
}

interface Directional {
  setup: SetupId
  category: OpportunityCategory
  side: 'long' | 'short'
  /** Stop distance as a fraction of price. */
  stop: number
  /** Target distance as a fraction of price. */
  target: number
  strength: number
  params: Record<string, string | number>
}

function directional(
  input: OpportunityInput,
  signal: MarketSignal,
  play: Directional
): TradeIdea | null {
  const relevance = input.relevance.get(signal.asset)
  const venue = venueFor(signal, relevance, input.accountsByExchange)
  if (!venue || !(signal.price > 0)) return null
  // Don't suggest the opposite of a position they already hold; that's a hedge, not an idea.
  const held = input.heldSides.get(signal.asset)
  if (held && held !== play.side) return null
  const sign = play.side === 'long' ? 1 : -1
  const stop = signal.price * (1 - sign * play.stop)
  const target = signal.price * (1 + sign * play.target)
  const notional = sizeForRisk(input.equityUsd, play.stop)
  const weight = relevance?.weight ?? MARKET_WEIGHT
  return {
    id: `${play.setup}:${signal.asset}`,
    kind: play.setup,
    bucket: 'opportunity',
    category: play.category,
    asset: signal.asset,
    symbol: venue.symbol,
    exchange: venue.exchange,
    orderPrompt: `${play.side === 'long' ? 'buy' : 'sell'} $${notional} ${venue.symbol} at market, stop loss at ${formatPrice(stop)}, take profit at ${formatPrice(target)}`,
    params: {
      asset: signal.asset,
      symbol: venue.symbol,
      side: play.side,
      notional,
      stop: formatPrice(stop),
      target: formatPrice(target),
      stopPct: Number((play.stop * 100).toFixed(1)),
      ...play.params,
    },
    levels: { entry: signal.price, stop, target },
    relevance: relevance?.reason,
    priority: clamp01(play.strength) * 50 + weight * 40,
  }
}

function pct(value: number | null, digits = 1): number {
  return Number((value ?? 0).toFixed(digits))
}

/** Every setup that fires for one coin. */
function setupsFor(input: OpportunityInput, signal: MarketSignal): TradeIdea[] {
  const out: (TradeIdea | null)[] = []
  const { flags } = signal
  const vol = Math.max(0.8, signal.stdev15m ?? 0.8) / 100
  const relevance = input.relevance.get(signal.asset)

  // Momentum: a move on rising open interest with the takers behind it.
  const up15 = signal.change15m ?? 0
  const oi1h = signal.oiChange1h ?? 0
  const takers = signal.takerBuyPct15m ?? 50
  if ((flags.has('pumping') || (up15 >= 1.5 && oi1h >= 3)) && takers >= 55) {
    out.push(
      directional(input, signal, {
        setup: 'breakout',
        category: 'momentum',
        side: 'long',
        stop: Math.max(0.015, vol * 1.5),
        target: Math.max(0.03, vol * 3),
        strength: up15 / 4 + oi1h / 20,
        params: { move: pct(up15), oi: pct(oi1h), takers: Math.round(takers) },
      })
    )
  }
  if ((flags.has('dumping') || (up15 <= -1.5 && oi1h >= 3)) && takers <= 45) {
    out.push(
      directional(input, signal, {
        setup: 'breakdown',
        category: 'momentum',
        side: 'short',
        stop: Math.max(0.015, vol * 1.5),
        target: Math.max(0.03, vol * 3),
        strength: -up15 / 4 + oi1h / 20,
        params: { move: pct(Math.abs(up15)), oi: pct(oi1h), takers: Math.round(100 - takers) },
      })
    )
  }

  // Mean reversion: a stretched day that has stopped extending (squeeze or one-sided wipeout).
  const day = signal.change24h ?? 0
  const liqOpposite = day > 0 ? (signal.liqShortUsd15m ?? 0) : (signal.liqLongUsd15m ?? 0)
  if (
    Math.abs(day) >= 10 &&
    (flags.has('squeeze') || liqOpposite > 0) &&
    Math.sign(up15) !== Math.sign(day)
  ) {
    out.push(
      directional(input, signal, {
        setup: 'overextended',
        category: 'meanReversion',
        side: day > 0 ? 'short' : 'long',
        stop: 0.03,
        target: Math.min(0.08, (Math.abs(day) / 100) * 0.35),
        strength: Math.abs(day) / 25,
        params: { move: pct(Math.abs(day)), direction: day > 0 ? 'up' : 'down' },
      })
    )
  }
  const funding = signal.fundingApr ?? 0
  if ((funding >= 60 || funding <= -40) && (signal.oiChange24h ?? 0) >= 5) {
    out.push(
      directional(input, signal, {
        setup: 'crowdedFunding',
        category: 'meanReversion',
        side: funding > 0 ? 'short' : 'long',
        stop: 0.04,
        target: 0.06,
        strength: Math.abs(funding) / 150,
        params: {
          apr: pct(Math.abs(funding), 0),
          crowd: funding > 0 ? 'long' : 'short',
          oi: pct(signal.oiChange24h),
        },
      })
    )
  }

  // Arbitrage: the same perp pays very different funding on two venues the user can trade.
  const tradable = signal.venues.filter(
    (venue) => venue.fundingApr !== null && input.accountsByExchange.has(venue.exchange)
  )
  const deepEnough = relevance !== undefined || (signal.oiUsd ?? 0) >= MIN_ARB_OI_USD
  if (tradable.length >= 2 && deepEnough) {
    const sorted = [...tradable].sort((a, b) => (b.fundingApr ?? 0) - (a.fundingApr ?? 0))
    const high = sorted[0]
    const low = sorted[sorted.length - 1]
    const spread = (high.fundingApr ?? 0) - (low.fundingApr ?? 0)
    if (spread >= MIN_ARB_SPREAD_APR && high.exchange !== low.exchange) {
      out.push({
        id: `fundingArb:${signal.asset}`,
        kind: 'fundingArb',
        bucket: 'opportunity',
        category: 'arbitrage',
        asset: signal.asset,
        symbol: high.symbol,
        exchange: high.exchange,
        route: 'arb',
        arb: {
          // Short where funding is highest (shorts collect it), long where it's lowest.
          short: {
            accountId: input.accountsByExchange.get(high.exchange)!,
            symbol: high.symbol,
            exchange: high.exchange,
          },
          long: {
            accountId: input.accountsByExchange.get(low.exchange)!,
            symbol: low.symbol,
            exchange: low.exchange,
          },
        },
        venueFunding: sorted.map((venue) => ({
          exchange: venue.exchange,
          symbol: venue.symbol,
          apr: venue.fundingApr ?? 0,
        })),
        params: {
          asset: signal.asset,
          spread: pct(spread),
          highApr: pct(high.fundingApr),
          lowApr: pct(low.fundingApr),
          highAbs: pct(Math.abs(high.fundingApr ?? 0)),
          lowAbs: pct(Math.abs(low.fundingApr ?? 0)),
        },
        levels: {},
        relevance: relevance?.reason,
        priority: clamp01(spread / 60) * 50 + (relevance?.weight ?? MARKET_WEIGHT) * 40 + 5,
      })
    }
  }

  // Fundamental: a run of ETF flows one way.
  const flows = input.etfFlows?.get(signal.asset)
  if (flows && flows.length >= 3) {
    const last = flows.slice(-3)
    const total = last.reduce((sum, value) => sum + value, 0)
    const sameWay = last.every((value) => value > 0) || last.every((value) => value < 0)
    const floor = signal.asset === 'BTC' ? 300e6 : 75e6
    if (sameWay && Math.abs(total) >= floor) {
      out.push(
        directional(input, signal, {
          setup: 'etfFlows',
          category: 'fundamental',
          side: total > 0 ? 'long' : 'short',
          stop: 0.05,
          target: 0.1,
          strength: Math.abs(total) / (floor * 4),
          params: {
            days: last.length,
            total: Math.round(total),
            direction: total > 0 ? 'in' : 'out',
          },
        })
      )
    }
  }

  // Fundamental: a big unlock coming for a coin they care about (not one they're long).
  const unlock = input.unlocks?.find(
    (event) =>
      event.asset === signal.asset &&
      event.at > input.now &&
      event.at - input.now <= UNLOCK_WINDOW_MS &&
      (event.pctOfMcap ?? 0) >= 1
  )
  if (unlock && relevance) {
    out.push(
      directional(input, signal, {
        setup: 'unlockPressure',
        category: 'fundamental',
        side: 'short',
        stop: 0.06,
        target: 0.1,
        strength: (unlock.pctOfMcap ?? 0) / 5,
        params: {
          value: Math.round(unlock.valueUsd),
          pct: pct(unlock.pctOfMcap, 2),
          days: Math.max(1, Math.round((unlock.at - input.now) / 86_400_000)),
        },
      })
    )
  }

  // News: a fresh headline on the coin and the price already moving with it.
  const headline = input.news?.find(
    (item) => item.asset === signal.asset && input.now - item.at <= NEWS_WINDOW_MS
  )
  if (headline && relevance && (Math.abs(day) >= 4 || flags.has('highVol'))) {
    out.push(
      directional(input, signal, {
        setup: 'newsMove',
        category: 'news',
        side: day >= 0 ? 'long' : 'short',
        stop: Math.max(0.02, vol * 2),
        target: Math.max(0.04, vol * 4),
        strength: Math.abs(day) / 12,
        params: { title: headline.title.slice(0, 120), move: pct(Math.abs(day)) },
      })
    )
  }

  return out.filter((idea): idea is TradeIdea => idea !== null)
}

/**
 * The best few opportunities for this user: the strongest setups on the coins that matter to
 * them, one per coin, at most two per family.
 */
export function buildOpportunities(input: OpportunityInput): TradeIdea[] {
  const candidates = input.signals.flatMap((signal) => setupsFor(input, signal))
  const byPriority = candidates.sort((a, b) => b.priority - a.priority)
  const oiOf = new Map(input.signals.map((signal) => [signal.asset, signal.oiUsd ?? 0]))
  const coins = new Set<string>()
  const perCategory = new Map<string, number>()
  const picked: TradeIdea[] = []
  for (const idea of byPriority) {
    // Unrelated coins only make it with a strong signal on a deep market.
    if (
      !idea.relevance &&
      (idea.priority < MARKET_MIN_PRIORITY || (oiOf.get(idea.asset) ?? 0) < MARKET_MIN_OI_USD)
    ) {
      continue
    }
    const category = idea.category ?? ''
    if (coins.has(idea.asset) || (perCategory.get(category) ?? 0) >= MAX_PER_CATEGORY) continue
    coins.add(idea.asset)
    perCategory.set(category, (perCategory.get(category) ?? 0) + 1)
    picked.push(idea)
    if (picked.length >= MAX_OPPORTUNITIES) break
  }
  return picked
}

/** Which setups fire right now, for the playbook's "live" marks. */
export function liveSetups(ideas: readonly TradeIdea[]): Set<string> {
  return new Set(ideas.map((idea) => idea.kind))
}
